import BoldText from '@/components/BoldText';
import { config } from '@/config';
import { ClassEnum, ROLE_SHOP_THEME, SETTING_TYPE } from '@/constants/enum';
import { PATH } from '@/constants/path';
import { isValidCSS } from '@/helpers';
import {
  useGetLocationsQuery,
  useGetShopThemesQuery,
  useGetWidgetAppearanceTextDetailQuery,
  useGetWidgetAppearanceTextQuery,
} from '@/redux/query';
import { handleSavePreviewOptionSettings, previewOptionSelector } from '@/redux/slice/preview.slice';
import { shippingSettingSelector } from '@/redux/slice/shipping.slice';
import { widgetColorSelector, widgetSettingSelector } from '@/redux/slice/widget.slice';
import { handleSaveWidgetTextDetail } from '@/redux/slice/widgetText.slice';
import { IListLocation } from '@/types/components/location';
import { BlockStack, Box, Button, Card, Divider, Icon, InlineStack, Scrollable, Select } from '@shopify/polaris';
import { ExternalSmallIcon, LanguageTranslateIcon, LightbulbIcon, MoonIcon, WandIcon } from '@shopify/polaris-icons';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import WidgetPreviewEmpty from '../WidgetPreviewEmpty';
import AdditionalMessage from './AdditionalMessage';
import DateTimeContainer from './DateTimeContainer';
import Header from './Header';
import RenderWidget from './RenderWidget';
import { Animation } from './styled/animation';
import { AppStyled } from './styled/app-styled';
import { BoxWrapper } from './styled/box-styled';
import { CustomButtonGroup, CustomHeading, CustomStyled } from './styled/custom-styled';
import { WidgetAppearanceError } from './ErrorMessage/WidgetAppearanceError';
import RequireMessage from './RequiredMessage';

export default function WidgetPreview() {
  const previewOptions = useSelector(previewOptionSelector);
  const widgetSettings = useSelector(widgetSettingSelector);
  const widgetColors = useSelector(widgetColorSelector);
  const [lightView, setLightView] = useState<boolean>(true);
  const { t } = useTranslation('widget_settings');
  const location = useLocation();

  const { data: fullWidgetAppearanceText } = useGetWidgetAppearanceTextQuery({
    shop: config.shop,
  });

  const { data: shopThemesData } = useGetShopThemesQuery({ shop: config.shop });

  const appEmbedId = process.env.REACT_APP_SHOPIFY_THEME_APP_EXTENSION_ID;

  const mainTheme = useMemo(() => {
    return shopThemesData?.find((item) => item.role === ROLE_SHOP_THEME.MAIN);
  }, [shopThemesData]);

  const isActiveEmbed = useMemo(() => {
    const file = mainTheme?.files.find((item) => item.filename === 'config/settings_data.json')?.file;
    const blocks = file?.current?.blocks;
    if (blocks && appEmbedId) {
      const appEmbedBlock = Object.values(blocks).find((block) => block.type.includes(appEmbedId));
      if (appEmbedBlock) {
        return !appEmbedBlock.disabled;
      }
    }
    return false;
  }, [appEmbedId, mainTheme?.files]);

  const translationWidgetOptions = useMemo(
    () =>
      fullWidgetAppearanceText?.data.appearanceText
        ?.filter((item) => item.status)
        .sort((a, b) => (b.defaultLanguage ? 1 : 0) - (a.defaultLanguage ? 1 : 0))
        .map((item) => {
          const languageLabel = item.defaultLanguage ? `${item.name} (Default language)` : item.name;
          const languageValue = item.defaultLanguage ? `${item.code}_default` : item.code;
          return {
            label: languageLabel,
            value: languageValue,
          };
        }),
    [fullWidgetAppearanceText?.data.appearanceText],
  );

  const [selectedLanguage, setSelectedLanguage] = useState(
    translationWidgetOptions?.find((item) => item.value.includes('default'))?.value,
  );

  const { data: widgetAppearanceTextDetail } = useGetWidgetAppearanceTextDetailQuery({
    shop: config.shop,
    code: selectedLanguage?.includes('default') ? selectedLanguage.split('_')[0] : selectedLanguage,
    defaultLanguage: selectedLanguage?.includes('default') || false,
  });

  const shippingSettings = useSelector(shippingSettingSelector);
  const dispatch = useDispatch();

  const { data: locationData } = useGetLocationsQuery({ shop: config.shop });

  const listLocations = useMemo(() => {
    return locationData?.data ? (JSON.parse(JSON.stringify(locationData.data)) as IListLocation[]) : [];
  }, [locationData?.data]);

  const isEnablePickup = useMemo(() => locationData?.data.some((item) => item.isEnablePickup), [locationData?.data]);

  const isEnableDelivery = useMemo(
    () => locationData?.data.some((item) => item.isEnableDelivery && item.settings.some((item) => item.isActive)),
    [locationData?.data],
  );

  const isEnableShipping = useMemo(() => shippingSettings.isActive, [shippingSettings]);

  useEffect(() => {
    const methodOrder = widgetSettings.sortMethods || [SETTING_TYPE.PICKUP, SETTING_TYPE.DELIVERY, SETTING_TYPE.SHIPPING];

    const enabledStatusMap: Record<string, boolean> = {
      [SETTING_TYPE.SHIPPING]: isEnableShipping,
      [SETTING_TYPE.DELIVERY]: isEnableDelivery || false,
      [SETTING_TYPE.PICKUP]: isEnablePickup || false,
    };

    const firstEnabledMethod = methodOrder.find((method) => enabledStatusMap[method]);

    const updatedMethods = [
      {
        key: SETTING_TYPE.SHIPPING,
        isEnable: isEnableShipping || false,
        selected: (isEnableShipping && firstEnabledMethod === SETTING_TYPE.SHIPPING) || false,
      },
      {
        key: SETTING_TYPE.DELIVERY,
        isEnable: isEnableDelivery || false,
        selected: (isEnableDelivery && firstEnabledMethod === SETTING_TYPE.DELIVERY) || false,
      },
      {
        key: SETTING_TYPE.PICKUP,
        isEnable: isEnablePickup || false,
        selected: (isEnablePickup && firstEnabledMethod === SETTING_TYPE.PICKUP) || false,
      },
    ];

    dispatch(handleSavePreviewOptionSettings({ methods: updatedMethods }));
  }, [dispatch, isEnableShipping, isEnableDelivery, isEnablePickup, widgetSettings.defaultMethod, widgetSettings.sortMethods]);

  useEffect(() => {
    if (widgetAppearanceTextDetail?.data) {
      dispatch(handleSaveWidgetTextDetail(widgetAppearanceTextDetail?.data));
    }
  }, [dispatch, widgetAppearanceTextDetail?.data]);

  const isExistSelectedMethod = useMemo(() => {
    const selectedMethod = previewOptions.methods.find((item) => item.selected);
    if (!selectedMethod) return false;

    if (selectedMethod.key === SETTING_TYPE.DELIVERY) {
      return previewOptions.isChecking && (!previewOptions.errorPostalCode || location.pathname === PATH.ONBOARDING.pathname);
    }

    return true;
  }, [previewOptions.methods, previewOptions.isChecking, previewOptions.errorPostalCode, location.pathname]);

  return (
    <BoxWrapper>
      <Card padding={'0'} background="avatar-five-bg-fill">
        <BlockStack>
          <Box padding={'300'}>
            <InlineStack blockAlign="center" align="space-between" gap={'200'}>
              <CustomHeading>
                <Box>
                  <Icon source={WandIcon} />
                </Box>
                <BoldText>{t('preview.title')}</BoldText>
              </CustomHeading>
              <CustomButtonGroup>
                <Select
                  label={<Icon source={LanguageTranslateIcon} />}
                  options={translationWidgetOptions}
                  value={selectedLanguage}
                  onChange={setSelectedLanguage}
                  labelInline
                  disabled={false}
                />
                <Button
                  variant={lightView ? 'primary' : 'secondary'}
                  icon={lightView ? LightbulbIcon : MoonIcon}
                  size={'micro'}
                  onClick={() => setLightView(!lightView)}
                >
                  {lightView ? 'Light' : 'Dark'}
                </Button>
                {isActiveEmbed && (
                  <Button
                    icon={ExternalSmallIcon}
                    size="micro"
                    variant="primary"
                    onClick={() => window.open(`https://${config.shop}`)}
                  >
                    See live
                  </Button>
                )}
              </CustomButtonGroup>
            </InlineStack>
          </Box>
          <Divider borderColor="border" />
          {previewOptions.methods.filter((item) => item.isEnable).length === 0 ? (
            <WidgetPreviewEmpty />
          ) : (
            <BlockStack>
              <Box background="bg-surface" maxWidth="312px">
                <Scrollable shadow style={{ maxHeight: '420px' }} scrollbarGutter={undefined} hint scrollbarWidth="thin">
                  <WidgetAppearanceError />
                  <Box
                    padding={'300'}
                    paddingBlockStart={'200'}
                    borderRadius="0"
                    background={lightView ? 'bg-surface' : 'bg-inverse'}
                  >
                    <Animation widgetSettings={widgetSettings} widgetColor={widgetColors}>
                      <AppStyled widgetSettings={widgetSettings} widgetColors={widgetColors}>
                        <CustomStyled
                          customCss={isValidCSS(widgetSettings?.additionalCss || '') ? widgetSettings?.additionalCss : ''}
                          lightView={lightView}
                        >
                          <div className={ClassEnum.Main}>
                            {<Header />}
                            <RenderWidget listLocations={listLocations} />
                            {isExistSelectedMethod && (
                              <>
                                <DateTimeContainer />
                                <AdditionalMessage type={previewOptions.methods.find((item) => item.selected)?.key} />
                              </>
                            )}
                            <RequireMessage />
                          </div>
                        </CustomStyled>
                      </AppStyled>
                    </Animation>
                  </Box>
                </Scrollable>
              </Box>
            </BlockStack>
          )}
        </BlockStack>
      </Card>
    </BoxWrapper>
  );
}
